import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowApi, agentApi} from '@/lib/api';
import { Zap, Plus, Play, Circle, ChevronRight, Search } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { PageGuide } from '@/components/PageGuide';

export const Route = createFileRoute('/_auth/workflows/')({ component: WorkflowsPage });

export default function WorkflowsPage() {
  const qc       = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch]           = useState('');
  const [showCreate, setShowCreate]   = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  const { data: workflows = [], isLoading } = useQuery({ queryKey: ['workflows'], queryFn: workflowApi.list });
  const { data: agents = [] }               = useQuery({ queryKey: ['agents'],    queryFn: agentApi.list });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => workflowApi.update(id, { active }),
    onSuccess: (_, vars) => { toast.success(vars.active ? 'Workflow activated' : 'Workflow deactivated'); qc.invalidateQueries({ queryKey: ['workflows'] }); },
  });
  const executeMutation = useMutation({
    mutationFn: (id: string) => workflowApi.execute(id),
    onSuccess: (exec) => navigate({ to: '/workflows/executions/$executionId', params: { executionId: exec.id } }),
  });

  const filtered = workflows.filter((wf) => {
    if (filterActive !== null && wf.active !== filterActive) return false;
    if (search && !wf.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="page">
      <PageGuide pageKey="workflows" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Workflows</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Autonomous multi-step agent behaviors and integrations</p>
        </div>
        <button
          id="new-workflow-btn"
          onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 12px var(--color-primary-glow)' }}
        >
          <Plus size={15} /> New Workflow
        </button>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search workflows…"
            style={{ width: '100%', padding: '8px 12px 8px 34px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {([null, true, false] as const).map((v) => (
          <button key={String(v)} onClick={() => setFilterActive(v)} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: filterActive === v ? 'var(--color-primary)' : '#1e2130',
            background: filterActive === v ? 'var(--color-primary-dim)' : 'transparent',
            color: filterActive === v ? 'var(--color-primary)' : '#64748b',
          }}>
            {v === null ? 'All' : v ? 'Active' : 'Inactive'}
          </button>
        ))}
      </div>

      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <Zap size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>{search ? `No workflows match "${search}"` : 'No workflows yet'}</p>
          {!search && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#334155' }}>Create a workflow to orchestrate multi-step automated interactions.</p>}
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {filtered.map((wf) => (
            <div key={wf.id} style={{ borderRadius: 14, border: '1px solid #1a1f35', background: '#0a0e1c', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* card header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: '#818cf8', background: '#6366f120', padding: '2px 8px', borderRadius: 20, border: '1px solid #6366f130' }}>
                  {wf.agent?.name ?? 'No Agent'}
                </span>
                <button
                  onClick={() => toggleMutation.mutate({ id: wf.id, active: !wf.active })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: wf.active ? '#10b98140' : '#47556940', background: wf.active ? '#10b98120' : '#47556920', color: wf.active ? '#34d399' : '#64748b' }}
                  title="Toggle active"
                >
                  <Circle size={6} fill="currentColor" /> {wf.active ? 'Active' : 'Inactive'}
                </button>
              </div>

              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{wf.name}</h3>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.5, flex: 1 }}>{wf.description ?? 'No description provided.'}</p>

              <div style={{ fontSize: 11, color: '#475569', display: 'flex', gap: 12 }}>
                <span>{wf.steps?.length ?? 0} steps</span>
                <span>v{wf.version}</span>
              </div>

              <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid #1a1f35' }}>
                <Link to="/workflows/$workflowId" params={{ workflowId: wf.id }}
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', border: '1px solid #2d3148', borderRadius: 8, color: '#94a3b8', fontSize: 12, textDecoration: 'none' }}>
                  Design <ChevronRight size={13} />
                </Link>
                <button
                  onClick={() => executeMutation.mutate(wf.id)}
                  disabled={executeMutation.isPending}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', border: '1px solid #6366f140', borderRadius: 8, background: '#6366f120', color: '#818cf8', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  title="Manually trigger a test execution"
                >
                  <Play size={11} fill="currentColor" /> Run
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateWorkflowDrawer
          agents={agents}
          onClose={() => setShowCreate(false)}
          onCreate={(body) => {
            workflowApi.create({ ...body, steps: [] }).then((wf) => {
              qc.invalidateQueries({ queryKey: ['workflows'] });
              setShowCreate(false);
              navigate({ to: '/workflows/$workflowId', params: { workflowId: wf.id } });
            });
          }}
        />
      )}
    </div>
  );
}

function CreateWorkflowDrawer({ agents, onClose, onCreate }: {
  agents: Array<{ id: string; name: string; status: string }>;
  onClose: () => void;
  onCreate: (data: { name: string; description: string; agentId: string | undefined; active: boolean }) => void;
}) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [agentId, setAgentId]         = useState('');
  const [isPending, setIsPending]     = useState(false);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 440, background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50, padding: '28px 24px', overflowY: 'auto', boxShadow: '-16px 0 60px rgba(0,0,0,0.6)', animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>New Workflow</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Name <span style={{ color: '#f87171' }}>*</span></label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Lead Qualification Flow" autoFocus style={inputStyle} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>AI Agent</label>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">— Reusable (No Agent) —</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.status})</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of the workflow goals…" rows={3}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button
            onClick={() => { setIsPending(true); onCreate({ name, description, agentId: agentId || undefined, active: true }); }}
            disabled={!name.trim() || isPending}
            style={{ flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!name.trim()) ? 0.5 : 1 }}
          >
            {isPending ? <Spinner size="sm" /> : 'Create & Design Steps'}
          </button>
        </div>
      </div>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
